import { useMutation, useQuery } from '@tanstack/react-query'
import { Button, SpinnerWithBackground, Text, TextArea } from '@viapeople/components'
import { useState } from 'react'
import { useParams } from 'react-router-dom'
import { trpc } from '../../utils/trpc'

/**
 * @fileoverview eNPS Response Page
 *
 * Public page for employees to submit their eNPS survey responses
 * Accessed via magic link from email
 */

export const EnpsResponsePage = () => {
	const { executionId } = useParams<{ executionId: string }>()
	const [selectedScore, setSelectedScore] = useState<number | null>(null)
	const [feedback, setFeedback] = useState('')
	const [isSubmitted, setIsSubmitted] = useState(false)

	// Fetch execution details
	const { data: execution, isLoading: isLoadingExecution } = useQuery({
		...trpc.public.enps.getExecution.queryOptions({
			executionId: executionId as string,
		}),
		enabled: !!executionId,
	})

	// Check if user already responded (Note: requires participantId from URL or session)
	// For now, we'll allow resubmission and let backend handle duplicates

	const submitMutation = useMutation({
		...trpc.public.enps.submitResponse.mutationOptions(),
		onSuccess: () => {
			setIsSubmitted(true)
		},
		onError: (error) => {
			const message = error instanceof Error ? error.message : 'Failed to submit response. Please try again.'
			alert(message)
		},
	})

	const handleScoreClick = (score: number) => {
		setSelectedScore(score)
	}

	const handleSubmit = async () => {
		if (selectedScore === null) {
			alert('Please select a score before submitting')
			return
		}

		// TODO: Get participantId from URL parameter or session
		// For now using a placeholder - this should come from the magic link URL
		const participantId = new URLSearchParams(window.location.search).get('participantId')

		if (!participantId) {
			alert('Invalid survey link. Please use the link from your email.')
			return
		}

		await submitMutation.mutateAsync({
			executionId: executionId as string,
			participantId,
			score: selectedScore,
			feedback: feedback.trim() || undefined,
		})
	}

	if (isLoadingExecution) {
		return (
			<div className='min-h-screen flex items-center justify-center bg-gray-50'>
				<SpinnerWithBackground />
			</div>
		)
	}

	if (!execution) {
		return (
			<div className='min-h-screen flex items-center justify-center bg-gray-50'>
				<div className='max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center'>
					<Text variant='large' weight='semibold' className='text-red-600 mb-4'>
						Survey Not Found
					</Text>
					<Text variant='small' className='text-gray-600'>
						This survey link is invalid or has expired.
					</Text>
				</div>
			</div>
		)
	}

	if (isSubmitted) {
		return (
			<div className='min-h-screen flex items-center justify-center bg-gray-50'>
				<div className='max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center'>
					<div className='mb-6'>
						<div className='w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4'>
							<svg
								className='w-8 h-8 text-green-600'
								fill='none'
								strokeLinecap='round'
								strokeLinejoin='round'
								strokeWidth='2'
								viewBox='0 0 24 24'
								stroke='currentColor'
								aria-hidden='true'>
								<path d='M5 13l4 4L19 7'></path>
							</svg>
						</div>
						<Text variant='large' weight='semibold' className='text-gray-900 mb-2'>
							Thank You!
						</Text>
						<Text variant='small' className='text-gray-600'>
							Your feedback has been submitted successfully.
						</Text>
					</div>
					<Text variant='xsmall' className='text-gray-500'>
						You can close this window now.
					</Text>
				</div>
			</div>
		)
	}

	const getScoreLabel = (score: number) => {
		if (score >= 9) return 'Very Likely'
		if (score >= 7) return 'Likely'
		if (score >= 5) return 'Neutral'
		if (score >= 3) return 'Unlikely'
		return 'Very Unlikely'
	}

	const getScoreColor = (score: number) => {
		if (score >= 9) return 'bg-green-500 hover:bg-green-600'
		if (score >= 7) return 'bg-blue-500 hover:bg-blue-600'
		if (score >= 5) return 'bg-yellow-500 hover:bg-yellow-600'
		if (score >= 3) return 'bg-orange-500 hover:bg-orange-600'
		return 'bg-red-500 hover:bg-red-600'
	}

	return (
		<div className='min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-12 px-4 sm:px-6 lg:px-8'>
			<div className='max-w-3xl mx-auto'>
				<div className='bg-white rounded-lg shadow-xl overflow-hidden'>
					{/* Header */}
					<div className='bg-gradient-to-r from-blue-600 to-indigo-600 px-8 py-6'>
						<Text variant='large' weight='semibold' className='text-white mb-2 text-2xl'>
							Feedback Survey
						</Text>
						<Text variant='small' className='text-blue-100'>
							Your feedback helps us improve our workplace
						</Text>
					</div>

					{/* Content */}
					<div className='px-8 py-8 space-y-8'>
						{/* Question */}
						<div>
							<Text variant='large' weight='semibold' className='text-gray-900 mb-2'>
								How likely are you to recommend our organization as a place to work?
							</Text>
							<Text variant='small' className='text-gray-600'>
								On a scale from 0 (Not at all likely) to 10 (Extremely likely)
							</Text>
						</div>

						{/* Score Selection */}
						<div>
							<div className='grid grid-cols-11 gap-2'>
								{Array.from({ length: 11 }, (_, i) => i).map((score) => (
									<button
										type='button'
										key={score}
										onClick={() => handleScoreClick(score)}
										className={`
                      relative aspect-square rounded-lg transition-all duration-200 transform
                      ${
												selectedScore === score
													? `${getScoreColor(score)} text-white scale-110 ring-4 ring-offset-2 ring-blue-400`
													: 'bg-gray-100 hover:bg-gray-200 text-gray-700'
											}
                      font-semibold text-lg flex items-center justify-center
                      focus:outline-none focus:ring-4 focus:ring-blue-300
                    `}>
										{score}
									</button>
								))}
							</div>

							{/* Score Labels */}
							<div className='flex justify-between mt-3 text-xs text-gray-600'>
								<span>Not at all likely</span>
								<span>Extremely likely</span>
							</div>

							{/* Selected Score Feedback */}
							{selectedScore !== null && (
								<div className='mt-4 p-4 bg-blue-50 rounded-lg text-center'>
									<Text variant='small' weight='semibold' className='text-blue-900'>
										You selected: {selectedScore} - {getScoreLabel(selectedScore)}
									</Text>
								</div>
							)}
						</div>

						{/* Feedback Text */}
						<div>
							<label htmlFor='feedback' className='block mb-2'>
								<Text variant='small' weight='semibold' className='text-gray-900'>
									What is the primary reason for your score? (Optional)
								</Text>
							</label>
							<TextArea
								id='feedback'
								value={feedback}
								onChange={(e) => setFeedback(e.target.value)}
								placeholder='Share your thoughts...'
								rows={5}
								className='w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent'
							/>
							<Text variant='xsmall' className='text-gray-500 mt-2'>
								Your feedback is valuable and will help us improve
							</Text>
						</div>

						{/* Submit Button */}
						<div className='flex justify-end pt-4'>
							<Button
								variant='primary'
								onClick={handleSubmit}
								disabled={selectedScore === null || submitMutation.isPending}
								className='px-8 py-3 text-lg'>
								{submitMutation.isPending ? 'Submitting...' : 'Submit Response'}
							</Button>
						</div>
					</div>

					{/* Footer */}
					<div className='bg-gray-50 px-8 py-4 border-t border-gray-200'>
						<Text variant='xsmall' className='text-gray-600 text-center'>
							Your responses are confidential and will be aggregated for analysis
						</Text>
					</div>
				</div>
			</div>
		</div>
	)
}
